import { useState, useEffect } from "react";
import { profileApi } from "../api/client";
import { C, fmt, pct, Card, Tag, ProgressBar } from "./theme";

const ROLE_ICONS = { "Self": "👨", "Spouse": "👩", "Child 1": "🧒", "Child 2": "🧒", "Parent 1": "👴", "Parent 2": "👵" };
const GOAL_ICON_OPTIONS = ["🎯", "🎓", "🏖", "🏡", "🚗", "💊", "💍", "🌱", "✈️", "👶"];
const GOAL_COLORS = [C.accent, C.blue, C.gold, C.purple, C.red, C.success, C.orange];

export default function FamilyScreen({ transactions, familyMembers, editMembers, setEditMembers, onSaveFamily, savingFamily,
  invite, setInvite, link, setLink, inviteEmail, setInviteEmail, joinCode, setJoinCode, sharingBusy, setSharingBusy, setError }) {

  const [goals, setGoals]         = useState([]);
  const [goalsLoaded, setGLoaded] = useState(false);
  const [editingGoal, setEditing] = useState(null);
  const [savingGoals, setSaving]  = useState(false);
  const [spouseExpanded, setSpouseExpanded] = useState(false);

  useEffect(() => {
    profileApi.getGoals().then(d => setGoals(d.goals || [])).catch(() => {}).finally(() => setGLoaded(true));
  }, []);

  // All roles — name editable inline on each card. Shows income/expense if any.
  const memberData = (editMembers || familyMembers).map(m => {
    const txns   = transactions.filter(t => t.member === m.role);
    const income = txns.filter(t => t.type === "income").reduce((s, t) => s + (t.amount || 0), 0);
    const expense = txns.filter(t => t.type === "expense").reduce((s, t) => s + (t.amount || 0), 0);
    return { role: m.role, name: m.name || "", income, expense };
  });

  const updateMemberName = (role, name) => {
    if (!editMembers) return;
    setEditMembers(prev => prev.map(m => m.role === role ? { ...m, name } : m));
  };

  // Auto-save family names on blur if changed
  const commitMemberName = async (role) => {
    const current = editMembers?.find(m => m.role === role)?.name?.trim() || "";
    const saved   = familyMembers?.find(m => m.role === role)?.name?.trim() || "";
    if (current === saved) return;
    await onSaveFamily();
  };

  const saveGoals = async (next) => {
    setSaving(true);
    try { const r = await profileApi.saveGoals(next); setGoals(r.goals); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addGoal = () => {
    const newGoal = { id: crypto.randomUUID(), name: "New goal", target: 100000, current: 0, years: 5, icon: "🎯", color: C.accent };
    setEditing(newGoal);
  };

  const persistGoal = () => {
    if (!editingGoal?.name?.trim()) return;
    const exists = goals.some(g => g.id === editingGoal.id);
    const next   = exists ? goals.map(g => g.id === editingGoal.id ? editingGoal : g) : [...goals, editingGoal];
    saveGoals(next);
    setEditing(null);
  };

  const deleteGoal = (id) => saveGoals(goals.filter(g => g.id !== id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Family member cards — names editable inline */}
      <div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, fontWeight: 600, letterSpacing: 0.5 }}>
          👨‍👩‍👧 FAMILY MEMBERS · click to name, leave blank to hide from forms
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          {memberData.map(m => {
            const net = m.income - m.expense;
            const hasActivity = m.income > 0 || m.expense > 0;
            return (
              <Card key={m.role} style={{ padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{ROLE_ICONS[m.role] || "👤"}</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>{m.role.toUpperCase()}</div>
                <input
                  value={m.name}
                  onChange={e => updateMemberName(m.role, e.target.value)}
                  onBlur={() => commitMemberName(m.role)}
                  placeholder={m.role === "Self" ? "Your name" : "Add name"}
                  disabled={savingFamily}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    borderBottom: `1px dashed ${C.border}`,
                    outline: "none",
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 13,
                    color: m.name ? C.text : C.muted,
                    fontFamily: "inherit",
                    marginBottom: 8,
                    padding: "2px 0",
                  }} />
                {m.income > 0  && <div style={{ fontSize: 12, color: C.success }}>+{fmt(m.income)}</div>}
                {m.expense > 0 && <div style={{ fontSize: 12, color: C.red }}>−{fmt(m.expense)}</div>}
                {!hasActivity  && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>No activity</div>}
                {m.income > 0  && <div style={{ fontSize: 12, fontWeight: 700, color: net >= 0 ? C.accent : C.red, marginTop: 4 }}>Net {fmt(net)}</div>}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Financial Goals */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>🎯 Family Financial Goals</div>
          <button onClick={addGoal} className="pill-outline-btn">
            + Add Goal
          </button>
        </div>

        {!goalsLoaded ? (
          <div style={{ color: C.muted, fontSize: 13, padding: 12 }}>Loading goals...</div>
        ) : goals.length === 0 && !editingGoal ? (
          <div style={{ color: C.muted, fontSize: 13, padding: 12, textAlign: "center" }}>
            No goals yet. Add your first family financial goal — child education, retirement, home, etc.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {goals.map(g => {
              const progress = pct(g.current, g.target);
              return (
                <div key={g.id} style={{ background: C.surface, borderRadius: 12, padding: 14, border: `1px solid ${g.color}33` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>{g.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{g.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{g.years} years · Target {fmt(g.target)}</div>
                    </div>
                    <Tag c={g.color}>{progress}%</Tag>
                  </div>
                  <ProgressBar value={g.current} max={g.target} color={g.color} height={8} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>Saved: {fmt(g.current)}</span>
                    <span style={{ fontSize: 11, color: g.color }}>Remaining: {fmt(Math.max(0, g.target - g.current))}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    <button onClick={() => setEditing(g)} style={{ flex: 1, padding: "5px 10px", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", fontSize: 11 }}>Edit</button>
                    <button onClick={() => deleteGoal(g.id)} style={{ padding: "5px 10px", background: C.red + "22", color: C.red, border: `1px solid ${C.red}33`, borderRadius: 6, cursor: "pointer", fontSize: 11 }}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editingGoal && (
          <div style={{ marginTop: 14, background: C.surface, borderRadius: 12, padding: 16, border: `1px solid ${editingGoal.color}66` }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <Field label="Goal Name" value={editingGoal.name}        onChange={v => setEditing({ ...editingGoal, name: v })} />
              <Field label="Target (₹)" type="number" value={editingGoal.target} onChange={v => setEditing({ ...editingGoal, target: Number(v) })} />
              <Field label="Current (₹)" type="number" value={editingGoal.current} onChange={v => setEditing({ ...editingGoal, current: Number(v) })} />
              <Field label="Years" type="number" value={editingGoal.years} onChange={v => setEditing({ ...editingGoal, years: Number(v) })} />
              <div>
                <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>ICON</label>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {GOAL_ICON_OPTIONS.map(i => (
                    <button key={i} onClick={() => setEditing({ ...editingGoal, icon: i })}
                      style={{ background: editingGoal.icon === i ? C.accentDim : "transparent", border: `1px solid ${editingGoal.icon === i ? C.accent : C.border}`, borderRadius: 6, padding: "4px 6px", cursor: "pointer", fontSize: 16 }}>{i}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, display: "block", marginBottom: 6 }}>COLOR</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {GOAL_COLORS.map(c => (
                    <button key={c} onClick={() => setEditing({ ...editingGoal, color: c })}
                      style={{ background: c, width: 24, height: 24, borderRadius: "50%", border: editingGoal.color === c ? `2px solid ${C.text}` : "1px solid transparent", cursor: "pointer" }} />
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={persistGoal} disabled={savingGoals} className="pill-outline-btn">
                {savingGoals ? "Saving..." : "Save Goal"}
              </button>
              <button onClick={() => setEditing(null)}
                style={{ padding: "8px 18px", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Spouse Access (collapsed by default) */}
      <Card>
        <div onClick={() => setSpouseExpanded(s => !s)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spouseExpanded ? 12 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>
            🔗 Spouse Access {link && <span style={{ color: C.accent, marginLeft: 6, fontSize: 11 }}>· Linked</span>}{invite && !link && <span style={{ color: C.gold, marginLeft: 6, fontSize: 11 }}>· Invite pending</span>}
          </div>
          <span style={{ fontSize: 12, color: C.muted }}>{spouseExpanded ? "▴ Hide" : "▾ Show"}</span>
        </div>

        {!spouseExpanded ? null : <>

        {link && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
            <div style={{ background: C.accent + "22", border: `1px solid ${C.accent}44`, borderRadius: 10, padding: 14, fontSize: 13, color: C.text }}>
              You are viewing your family's shared finances.
            </div>
            <button onClick={async () => { setSharingBusy(true); try { await profileApi.unlink(); setLink(null); } catch (e) { setError(e.message); } finally { setSharingBusy(false); } }}
              disabled={sharingBusy}
              style={{ padding: "9px 20px", background: C.red + "22", color: C.red, border: `1px solid ${C.red}44`, borderRadius: 24, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", fontSize: 13, alignSelf: "flex-start" }}>
              {sharingBusy ? "Unlinking..." : "Unlink from Family"}
            </button>
          </div>
        )}

        {!link && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
            <div style={{ fontSize: 12, color: C.muted }}>Invite your spouse to view and add transactions to your shared family finances.</div>

            {invite && (
              <div style={{ background: C.gold + "22", border: `1px solid ${C.gold}44`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Invite sent to <strong style={{ color: C.text }}>{invite.spouseEmail}</strong>. Share this code with them:</div>
                <div style={{ fontFamily: "monospace", fontSize: 13, background: C.surface, padding: "8px 12px", borderRadius: 8, color: C.accent, wordBreak: "break-all", userSelect: "all" }}>{invite.inviteCode}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Spouse signs up at trackfin.contextreach.com, goes to Family → Spouse Access, and enters this code.</div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{invite ? "UPDATE INVITE EMAIL" : "INVITE SPOUSE"}</div>
              <div className="invite-row">
                <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="spouse@email.com" type="email"
                  style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                <button onClick={async () => { setSharingBusy(true); try { const r = await profileApi.createInvite(inviteEmail); setInvite(r); setInviteEmail(""); } catch (e) { setError(e.message); } finally { setSharingBusy(false); } }}
                  disabled={sharingBusy || !inviteEmail}
                  style={{ padding: "10px 18px", background: C.accent, color: "#fff", border: "none", borderRadius: 24, fontWeight: 600, cursor: sharingBusy || !inviteEmail ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 13, opacity: sharingBusy || !inviteEmail ? 0.6 : 1, whiteSpace: "nowrap" }}>
                  {sharingBusy ? "..." : "Send Invite"}
                </button>
              </div>
            </div>

            <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 8 }}>JOIN A FAMILY (enter code from your spouse)</div>
              <div className="join-row">
                <input value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="Paste invite code here"
                  style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 12, fontFamily: "monospace", outline: "none" }} />
                <button onClick={async () => { setSharingBusy(true); try { const r = await profileApi.joinFamily(joinCode.trim()); setLink({ linkedUserId: r.linkedTo }); setJoinCode(""); } catch (e) { setError(e.message); } finally { setSharingBusy(false); } }}
                  disabled={sharingBusy || !joinCode}
                  style={{ padding: "10px 18px", background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 24, fontWeight: 600, cursor: sharingBusy || !joinCode ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 13, opacity: sharingBusy || !joinCode ? 0.6 : 1, whiteSpace: "nowrap" }}>
                  {sharingBusy ? "..." : "Join Family"}
                </button>
              </div>
            </div>
          </div>
        )}
        </>}
      </Card>
    </div>
  );
}

function Field({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, display: "block" }}>{label.toUpperCase()}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
    </div>
  );
}
